/**
 * Consent Mode v2 durumu.
 *
 * Varsayılanlar index.html'de, GTM snippet'inden ÖNCE yazılıyor — burada
 * yalnızca kullanıcının cevabı ve o cevabın Google'a iletilmesi var. Sıra
 * önemli: varsayılanlar container'dan sonra yazılırsa tüm tag'ler çoktan
 * rızasız çalışmış oluyor.
 *
 * Cevap ÇEREZDE tutuluyor, localStorage'da değil. Reklamlar
 * home.chatharmony.ai'ye düşüyor ve kullanıcı oradan chatharmony.ai'ye
 * geçiyor; localStorage origin'e bağlı olduğu için o geçişte cevap
 * kayboluyordu — kullanıcıya iki kez soruluyor, AB'de ise uygulama tarafı
 * kullanıcı zaten kabul etmişken "denied" ile başlıyordu.
 */

export const CONSENT_STORAGE_KEY = "harmony_consent";

// Rıza kaydının ömrü. Google'ın önerisi 6-12 ay arası; sürenin sonunda
// kullanıcıya yeniden soruluyor.
const CONSENT_TTL_MS = 180 * 24 * 60 * 60 * 1000;
const CONSENT_TTL_SECONDS = CONSENT_TTL_MS / 1000;

// Rıza metninin sürümü. Kapsam değişirse artırılır ve eski kayıtlar geçersiz
// sayılır — kullanıcı kabul etmediği bir şeye kabul etmiş sayılamaz.
const CONSENT_VERSION = 1;

/**
 * Banner yeniden açıldığında yayılan olay. Ayarlardan "çerez tercihleri"
 * seçildiğinde banner'ın tekrar görünmesini sağlıyor.
 */
export const CONSENT_REOPEN_EVENT = "harmony:consent-reopen";

interface StoredConsent {
  granted: boolean;
  at: number;
}

type ConsentSignal =
  | "ad_storage"
  | "ad_user_data"
  | "ad_personalization"
  | "analytics_storage"
  | "personalization_storage";

// Banner'ın karar verdiği sinyaller. `functionality_storage` ve
// `security_storage` zorunlu kategoriler; index.html'de kalıcı olarak
// "granted" ve buradan hiç değiştirilmiyor.
const MANAGED_SIGNALS: ConsentSignal[] = [
  "ad_storage",
  "ad_user_data",
  "ad_personalization",
  "analytics_storage",
  "personalization_storage",
];

declare global {
  interface Window {
    // index.html'deki consent bloğu tanımlıyor; script engellenirse olmayabilir.
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Çerezin yazılacağı alan adı. Alt alan adları arasında paylaşılması için
 * nokta ile başlıyor; localhost ve tünel adreslerinde `domain` verilemez
 * (tarayıcı çerezi tamamen reddeder), o yüzden host'a bırakılıyor.
 */
const cookieDomain = (): string => {
  try {
    const host = window.location.hostname;
    return host === "chatharmony.ai" || host.endsWith(".chatharmony.ai")
      ? "; domain=.chatharmony.ai"
      : "";
  } catch {
    return "";
  }
};

/**
 * Değer biçimi: `v<sürüm>.<0|1>.<epochMs>` — ör. `v1.1.1789030042000`.
 *
 * JSON değil, çünkü aynı değeri index.html içindeki satır içi script de
 * (GTM'den önce, React yüklenmeden) okumak zorunda. Çerez değerinde virgül
 * ve süslü parantez kaçış gerektiriyor; bu biçim hiç kaçış istemiyor.
 */
const encode = (record: StoredConsent): string =>
  `v${CONSENT_VERSION}.${record.granted ? 1 : 0}.${record.at}`;

const decode = (raw: string | undefined): StoredConsent | null => {
  if (!raw) return null;
  const match = /^v(\d+)\.([01])\.(\d+)$/.exec(raw);
  if (!match) return null;
  if (Number(match[1]) !== CONSENT_VERSION) return null;
  const at = Number(match[3]);
  if (!at || Date.now() - at > CONSENT_TTL_MS) return null;
  return { granted: match[2] === "1", at };
};

const readCookie = (): string | undefined => {
  try {
    const match = document.cookie.match(
      new RegExp(`(?:^|;\\s*)${CONSENT_STORAGE_KEY}=([^;]*)`),
    );
    return match ? decodeURIComponent(match[1]) : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Çereze geçmeden önce verilmiş cevaplar. Bir kez okunup çereze taşınıyor;
 * kullanıcı daha önce reddettiyse o tercih korunmalı, "kayıt yok" sayılıp
 * sessizce izinli duruma düşmemeli.
 */
const readLegacyLocalStorage = (): StoredConsent | null => {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredConsent & { version?: number };
    if (parsed?.version !== CONSENT_VERSION) return null;
    if (typeof parsed.granted !== "boolean") return null;
    if (!parsed.at || Date.now() - parsed.at > CONSENT_TTL_MS) return null;
    return { granted: parsed.granted, at: parsed.at };
  } catch {
    return null;
  }
};

const writeCookie = (record: StoredConsent): void => {
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie =
    `${CONSENT_STORAGE_KEY}=${encode(record)}` +
    `; path=/${cookieDomain()}; max-age=${CONSENT_TTL_SECONDS}` +
    `; SameSite=Lax${secure}`;
};

const readStored = (): StoredConsent | null => {
  const fromCookie = decode(readCookie());
  if (fromCookie) return fromCookie;

  // Eski kayıt varsa çereze taşı; bundan sonra alt alan adları da görüyor.
  const legacy = readLegacyLocalStorage();
  if (legacy) {
    try {
      writeCookie(legacy);
    } catch {
      // Çerez yazılamıyorsa cevap yine geçerli, sadece paylaşılamıyor.
    }
    return legacy;
  }

  return null;
};

/**
 * Kullanıcı daha önce cevap verdi mi? Vermediyse banner gösterilir.
 * Çerez engelliyse her zaman `false` döner; banner her ziyarette görünür ama
 * rıza da hiçbir zaman yanlışlıkla varsayılmaz.
 */
export const hasConsentDecision = (): boolean => readStored() !== null;

export const isConsentGranted = (): boolean => readStored()?.granted === true;

const pushConsentUpdate = (granted: boolean): void => {
  try {
    const state = granted ? "granted" : "denied";
    const payload = Object.fromEntries(
      MANAGED_SIGNALS.map((signal) => [signal, state]),
    );
    window.gtag?.("consent", "update", payload);
  } catch (e) {
    console.warn("consent update error", e);
  }
};

/**
 * Cevabı kaydeder ve Google'a iletir.
 *
 * `consent update` çağrısı bekleyen tag'leri serbest bırakıyor; gtag tanımlı
 * değilse (script engelli) kayıt yine de tutuluyor, böylece kullanıcıya
 * tekrar sorulmuyor.
 */
export const setConsent = (granted: boolean): void => {
  try {
    writeCookie({ granted, at: Date.now() });
    // Eski kaydı bırakmıyoruz; kalırsa çerez süresi dolduğunda geri gelip
    // kullanıcının güncel tercihini eziyor.
    localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch (e) {
    console.warn("consent persist error", e);
  }

  pushConsentUpdate(granted);
};

/**
 * Kullanıcının kararını geri alıp banner'ı yeniden gösterir.
 *
 * Rıza geri alınabilir ve yeniden verilebilir olmak zorunda. Bu olmadan bir
 * kez "yalnızca gerekli" diyen kullanıcı, fikrini değiştirse bile 180 gün
 * boyunca kapalı kalıyordu ve arayüzde bunu değiştirecek hiçbir yer yoktu.
 */
export const reopenConsentBanner = (): void => {
  try {
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie =
      `${CONSENT_STORAGE_KEY}=` +
      `; path=/${cookieDomain()}; max-age=0; SameSite=Lax${secure}`;
    localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch (e) {
    console.warn("consent reset error", e);
  }

  // Karar silindi; bölgesel varsayılanlar yeniden geçerli ama mevcut sayfada
  // tag'ler zaten çalışıyor. En güvenli davranış: kullanıcı yeniden
  // cevaplayana kadar reddedilmiş saymak.
  pushConsentUpdate(false);

  try {
    window.dispatchEvent(new CustomEvent(CONSENT_REOPEN_EVENT));
  } catch {
    // CustomEvent yoksa banner bir sonraki yüklemede görünür.
  }
};
